export const DEFAULT_COMMUNICATIONS_SETTINGS = {
    outboundBubbleColor: '#2563EB',
    inboundBubbleColor: '#64748B',
    flagFollowupColor: '#F97316',
    flagFollowthruColor: '#DC2626',
    flagUrgentColor: '#B91C1C',
    flagAsapColor: '#DC2626',
    flagNeedsAttentionColor: '#F59E0B',
    flagResolvedColor: '#00FF00',
    flagClosedColor: '#6B7280',
    allowSubjectLine: false,
    // 1.12.212-beta: check a phone number's line type (Twilio Lookup) when it is first added or used.
    checkNumbersAutomatically: true,
    // Review fix round 1: the most paid number checks per company per day, Test or Live (enforced on the server).
    numberCheckDailyLimit: 200,
    reconnectionMinutes: 15,
    newContactHandling: 'autosave',
    threadSortOrder: 'recent',
    groupThreadsByAccount: false,
    groupThreadsByStatus: false,
    statusCategoryOrder: ['escalated', 'active', 'pending', 'resolved'],
    threadReorderMode: 'drag_drop',
    pinnedThreadPosition: 'top',
    channelActiveHours: 8,
    channelCoolingHours: 16,
    handoffNudgeMinutes: 120,
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const NEW_CONTACT_HANDLING = ['autosave', 'ask'];
const THREAD_SORT_ORDERS = ['recent', 'first_name', 'last_name'];
const THREAD_REORDER_MODES = ['drag_drop', 'numbered'];
const PINNED_THREAD_POSITIONS = ['top', 'bottom'];
const clampInteger = (value, fallback, min, max) => {
    const next = Number.parseInt(value, 10);
    if (!Number.isFinite(next)) return fallback;
    return Math.min(max, Math.max(min, next));
};
export const STATUS_CATEGORY_LABELS = {
    escalated: 'Escalated',
    active: 'Active',
    pending: 'Pending',
    resolved: 'Resolved',
};
export const COMMUNICATIONS_PANEL_BACKGROUNDS = {
    light: '#FFFFFF',
    dark: '#1F2937',
};
export const BUBBLE_TEXT_COLORS = {
    inbound: '#FFFFFF',
    outbound: '#FFFFFF',
};
export const MIN_BUBBLE_TEXT_CONTRAST = 4.5;
export const MIN_BUBBLE_PANEL_CONTRAST = 2.5;

function cleanHex(value) {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    return HEX_COLOR.test(withHash) ? withHash.toUpperCase() : '';
}

export function normalizeStatusCategoryOrder(value) {
    const raw = Array.isArray(value) ? value : DEFAULT_COMMUNICATIONS_SETTINGS.statusCategoryOrder;
    const seen = new Set();
    const normalized = raw
        .map((item) => (typeof item === 'string' ? item.trim().toLowerCase() : ''))
        .filter((item) => {
            if (!item || seen.has(item)) return false;
            seen.add(item);
            return true;
        });
    DEFAULT_COMMUNICATIONS_SETTINGS.statusCategoryOrder.forEach((item) => {
        if (!seen.has(item)) normalized.push(item);
    });
    return normalized.length ? normalized : DEFAULT_COMMUNICATIONS_SETTINGS.statusCategoryOrder;
}

function hexToRgb(hex) {
    const cleaned = cleanHex(hex);
    if (!cleaned) return null;
    const h = cleaned.slice(1);
    return [0, 2, 4].map((index) => parseInt(h.slice(index, index + 2), 16));
}

function relLum(r, g, b) {
    const [rr, gg, bb] = [r, g, b].map((v) => {
        const channel = v / 255;
        return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
}

export function contrastHexes(firstHex, secondHex) {
    const first = hexToRgb(firstHex);
    const second = hexToRgb(secondHex);
    if (!first || !second) return 0;
    const [lighter, darker] = [relLum(...first), relLum(...second)].sort((a, z) => z - a);
    return (lighter + 0.05) / (darker + 0.05);
}

export function contrastHexWithWhite(hex) {
    return contrastHexes(hex, '#FFFFFF');
}

export function validateBubbleColor(hex, { textHex = '#FFFFFF', label = 'Bubble color' } = {}) {
    const cleaned = cleanHex(hex);
    if (!cleaned) {
        return {
            color: '',
            ok: false,
            textContrast: 0,
            lightPanelContrast: 0,
            darkPanelContrast: 0,
            messages: [`${label} must be a 6-digit hex color.`],
        };
    }
    const textContrast = contrastHexes(cleaned, textHex);
    const lightPanelContrast = contrastHexes(cleaned, COMMUNICATIONS_PANEL_BACKGROUNDS.light);
    const darkPanelContrast = contrastHexes(cleaned, COMMUNICATIONS_PANEL_BACKGROUNDS.dark);
    const messages = [];
    if (textContrast < MIN_BUBBLE_TEXT_CONTRAST) {
        messages.push(`${label} needs at least ${MIN_BUBBLE_TEXT_CONTRAST}:1 contrast with its message text.`);
    }
    if (lightPanelContrast < MIN_BUBBLE_PANEL_CONTRAST) {
        messages.push(`${label} is too close to the light Communications panel background.`);
    }
    if (darkPanelContrast < MIN_BUBBLE_PANEL_CONTRAST) {
        messages.push(`${label} is too close to the dark Communications panel background.`);
    }
    return {
        color: cleaned,
        ok: messages.length === 0,
        textContrast,
        lightPanelContrast,
        darkPanelContrast,
        messages,
    };
}

export function validateCommunicationsBubbleColors(settings = {}) {
    const outbound = validateBubbleColor(settings.outboundBubbleColor || settings.outbound_bubble_color, {
        textHex: BUBBLE_TEXT_COLORS.outbound,
        label: 'Outbound bubble color',
    });
    const inbound = validateBubbleColor(settings.inboundBubbleColor || settings.inbound_bubble_color, {
        textHex: BUBBLE_TEXT_COLORS.inbound,
        label: 'Inbound bubble color',
    });
    return {
        outbound,
        inbound,
        ok: outbound.ok && inbound.ok,
        messages: [...outbound.messages, ...inbound.messages],
    };
}

export function normalizeCommunicationsSettings(row = {}) {
    const outboundBubbleColor = cleanHex(row?.outbound_bubble_color || row?.outboundBubbleColor)
        || DEFAULT_COMMUNICATIONS_SETTINGS.outboundBubbleColor;
    const inboundBubbleColor = cleanHex(row?.inbound_bubble_color || row?.inboundBubbleColor)
        || DEFAULT_COMMUNICATIONS_SETTINGS.inboundBubbleColor;
    const normalizedOutbound = validateBubbleColor(outboundBubbleColor, {
        textHex: BUBBLE_TEXT_COLORS.outbound,
        label: 'Outbound bubble color',
    }).ok ? outboundBubbleColor : DEFAULT_COMMUNICATIONS_SETTINGS.outboundBubbleColor;
    const normalizedInbound = validateBubbleColor(inboundBubbleColor, {
        textHex: BUBBLE_TEXT_COLORS.inbound,
        label: 'Inbound bubble color',
    }).ok ? inboundBubbleColor : DEFAULT_COMMUNICATIONS_SETTINGS.inboundBubbleColor;
    return {
        outboundBubbleColor: normalizedOutbound,
        inboundBubbleColor: normalizedInbound,
        flagFollowupColor: cleanHex(row?.flag_followup_color || row?.flagFollowupColor)
            || DEFAULT_COMMUNICATIONS_SETTINGS.flagFollowupColor,
        flagFollowthruColor: cleanHex(row?.flag_followthru_color || row?.flagFollowthruColor)
            || DEFAULT_COMMUNICATIONS_SETTINGS.flagFollowthruColor,
        flagUrgentColor: cleanHex(row?.flag_urgent_color || row?.flagUrgentColor)
            || DEFAULT_COMMUNICATIONS_SETTINGS.flagUrgentColor,
        flagAsapColor: cleanHex(row?.flag_asap_color || row?.flagAsapColor)
            || DEFAULT_COMMUNICATIONS_SETTINGS.flagAsapColor,
        flagNeedsAttentionColor: cleanHex(row?.flag_needs_attention_color || row?.flagNeedsAttentionColor)
            || DEFAULT_COMMUNICATIONS_SETTINGS.flagNeedsAttentionColor,
        flagResolvedColor: cleanHex(row?.flag_resolved_color || row?.flagResolvedColor)
            || DEFAULT_COMMUNICATIONS_SETTINGS.flagResolvedColor,
        flagClosedColor: cleanHex(row?.flag_closed_color || row?.flagClosedColor)
            || DEFAULT_COMMUNICATIONS_SETTINGS.flagClosedColor,
        allowSubjectLine: Boolean(row?.allow_subject_line ?? row?.allowSubjectLine ?? DEFAULT_COMMUNICATIONS_SETTINGS.allowSubjectLine),
        checkNumbersAutomatically: Boolean(row?.check_numbers_automatically ?? row?.checkNumbersAutomatically ?? DEFAULT_COMMUNICATIONS_SETTINGS.checkNumbersAutomatically),
        reconnectionMinutes: clampInteger(row?.reconnection_minutes ?? row?.reconnectionMinutes, 15, 1, 1440),
        numberCheckDailyLimit: clampInteger(
            row?.number_check_daily_limit ?? row?.numberCheckDailyLimit,
            DEFAULT_COMMUNICATIONS_SETTINGS.numberCheckDailyLimit,
            0,
            10000,
        ),
        newContactHandling: NEW_CONTACT_HANDLING.includes(row?.new_contact_handling || row?.newContactHandling)
            ? (row?.new_contact_handling || row?.newContactHandling)
            : DEFAULT_COMMUNICATIONS_SETTINGS.newContactHandling,
        threadSortOrder: THREAD_SORT_ORDERS.includes(row?.thread_sort_order || row?.threadSortOrder)
            ? (row?.thread_sort_order || row?.threadSortOrder)
            : DEFAULT_COMMUNICATIONS_SETTINGS.threadSortOrder,
        groupThreadsByAccount: Boolean(row?.group_threads_by_account ?? row?.groupThreadsByAccount ?? DEFAULT_COMMUNICATIONS_SETTINGS.groupThreadsByAccount),
        groupThreadsByStatus: Boolean(row?.group_threads_by_status ?? row?.groupThreadsByStatus ?? DEFAULT_COMMUNICATIONS_SETTINGS.groupThreadsByStatus),
        statusCategoryOrder: normalizeStatusCategoryOrder(row?.status_category_order || row?.statusCategoryOrder),
        threadReorderMode: THREAD_REORDER_MODES.includes(row?.thread_reorder_mode || row?.threadReorderMode)
            ? (row?.thread_reorder_mode || row?.threadReorderMode)
            : DEFAULT_COMMUNICATIONS_SETTINGS.threadReorderMode,
        pinnedThreadPosition: PINNED_THREAD_POSITIONS.includes(row?.pinned_thread_position || row?.pinnedThreadPosition)
            ? (row?.pinned_thread_position || row?.pinnedThreadPosition)
            : DEFAULT_COMMUNICATIONS_SETTINGS.pinnedThreadPosition,
        channelActiveHours: clampInteger(
            row?.channel_active_hours ?? row?.channelActiveHours,
            DEFAULT_COMMUNICATIONS_SETTINGS.channelActiveHours,
            1,
            72
        ),
        channelCoolingHours: clampInteger(
            row?.channel_cooling_hours ?? row?.channelCoolingHours,
            DEFAULT_COMMUNICATIONS_SETTINGS.channelCoolingHours,
            2,
            168
        ),
        handoffNudgeMinutes: clampInteger(
            row?.handoff_nudge_minutes ?? row?.handoffNudgeMinutes,
            DEFAULT_COMMUNICATIONS_SETTINGS.handoffNudgeMinutes,
            15,
            10080
        ),
    };
}

// Review fix round 2: the daily number-check limit is only sent by an Owner / Tenant Admin. Anyone else's save leaves the
// column out, so it can never trip the database's "only admins change the limit" guard (an upsert is an INSERT … ON
// CONFLICT, and the guard sees the INSERT first).
export function buildCommunicationsSettingsUpsert({ orgId, values, includeNumberCheckDailyLimit = true } = {}) {
    const settings = normalizeCommunicationsSettings(values);
    const row = {
        org_id: orgId,
        outbound_bubble_color: settings.outboundBubbleColor,
        inbound_bubble_color: settings.inboundBubbleColor,
        flag_followup_color: settings.flagFollowupColor,
        flag_followthru_color: settings.flagFollowthruColor,
        flag_urgent_color: settings.flagUrgentColor,
        flag_asap_color: settings.flagAsapColor,
        flag_needs_attention_color: settings.flagNeedsAttentionColor,
        flag_resolved_color: settings.flagResolvedColor,
        flag_closed_color: settings.flagClosedColor,
        allow_subject_line: settings.allowSubjectLine,
        check_numbers_automatically: settings.checkNumbersAutomatically,
        number_check_daily_limit: settings.numberCheckDailyLimit,
        reconnection_minutes: settings.reconnectionMinutes,
        new_contact_handling: settings.newContactHandling,
        thread_sort_order: settings.threadSortOrder,
        group_threads_by_account: settings.groupThreadsByAccount,
        group_threads_by_status: settings.groupThreadsByStatus,
        status_category_order: settings.statusCategoryOrder,
        thread_reorder_mode: settings.threadReorderMode,
        pinned_thread_position: settings.pinnedThreadPosition,
        channel_active_hours: settings.channelActiveHours,
        channel_cooling_hours: settings.channelCoolingHours,
        handoff_nudge_minutes: settings.handoffNudgeMinutes,
    };
    if (!includeNumberCheckDailyLimit) delete row.number_check_daily_limit;
    return row;
}

export function buildCommunicationsCssVars(settings = {}) {
    const normalized = normalizeCommunicationsSettings(settings);
    const vars = {
        '--comms-bubble-outbound': normalized.outboundBubbleColor,
        '--comms-bubble-inbound': normalized.inboundBubbleColor,
        '--comms-flag-followup': normalized.flagFollowupColor,
        '--comms-flag-followthru': normalized.flagFollowthruColor,
        '--comms-flag-urgent': normalized.flagUrgentColor,
        '--comms-flag-asap': normalized.flagAsapColor,
        '--comms-flag-needs-attention': normalized.flagNeedsAttentionColor,
        '--comms-flag-resolved': normalized.flagResolvedColor,
        '--comms-flag-closed': normalized.flagClosedColor,
    };
    return vars;
}

// BL-177 Gate 17 / 17b — per-user message-preview lines for the conversation list (Outlook-style,
// default 1). Stored in this browser only, keyed by staff user id. Gate 17b moved the control from
// a gear on the list into the Communications settings modal; the saved key is unchanged.
export const PREVIEW_LINE_OPTIONS = [
    [0, 'No preview'],
    [1, '1 line'],
    [2, '2 lines'],
    [3, '3 lines'],
];
export const DEFAULT_PREVIEW_LINES = 1;

const previewLinesKey = (userId) => `communications-preview-lines:${userId}`;

/** Reads the saved preview-line count for this user; honors the older show/hide toggle's "false". */
export function readPreviewLines(userId) {
    if (!userId || typeof window === 'undefined') return DEFAULT_PREVIEW_LINES;
    try {
        const saved = window.localStorage.getItem(previewLinesKey(userId));
        if (saved !== null) {
            const parsed = Number(saved);
            if (PREVIEW_LINE_OPTIONS.some(([value]) => value === parsed)) return parsed;
        }
        if (window.localStorage.getItem(`communications-show-preview:${userId}`) === 'false') return 0;
    } catch { /* storage blocked — fall back to the default */ }
    return DEFAULT_PREVIEW_LINES;
}

export function writePreviewLines(userId, lines) {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(previewLinesKey(userId), String(lines));
    } catch { /* storage blocked — the choice still applies for this visit */ }
}

// ---------------------------------------------------------------------------------------------------------------------------
// Review fix round 1 (deploy ordering): the website can go live before migration 281 is applied. Until then these columns do
// not exist, so a load or save that names them fails. The view retries without them (defaults apply: checks ON, limit 200).
// ---------------------------------------------------------------------------------------------------------------------------
export const COMMUNICATIONS_SETTINGS_MIGRATION_281_COLUMNS = ['check_numbers_automatically', 'number_check_daily_limit'];

export const COMMUNICATIONS_SETTINGS_SELECT = 'outbound_bubble_color, inbound_bubble_color, flag_followup_color, flag_followthru_color, flag_urgent_color, flag_asap_color, flag_needs_attention_color, flag_resolved_color, flag_closed_color, allow_subject_line, reconnection_minutes, check_numbers_automatically, number_check_daily_limit, new_contact_handling, thread_sort_order, group_threads_by_account, group_threads_by_status, status_category_order, thread_reorder_mode, pinned_thread_position, channel_active_hours, channel_cooling_hours, handoff_nudge_minutes';

export const COMMUNICATIONS_SETTINGS_SELECT_BEFORE_281 = COMMUNICATIONS_SETTINGS_SELECT.split(', ')
    .filter((column) => !COMMUNICATIONS_SETTINGS_MIGRATION_281_COLUMNS.includes(column)).join(', ');

/** True when a PostgREST / Postgres error says one of migration 281's settings columns does not exist yet. */
export function isMissingMigration281ColumnError(error) {
    if (!error) return false;
    const text = `${error.message || ''} ${error.details || ''} ${error.hint || ''}`;
    const namesColumn = COMMUNICATIONS_SETTINGS_MIGRATION_281_COLUMNS.some((column) => text.includes(column));
    return namesColumn && (error.code === '42703' || error.code === 'PGRST204' || /column|schema cache/i.test(text));
}

/**
 * The settings upsert without migration 281's columns (for a database that does not have them yet). Only those two columns
 * are dropped — they arrive together in one migration, so both are missing or neither is; every other setting is still saved.
 * Review fix round 2: a non-admin's payload never had the limit, and stays without it.
 */
export function withoutMigration281Columns(payload) {
    const next = { ...(payload || {}) };
    COMMUNICATIONS_SETTINGS_MIGRATION_281_COLUMNS.forEach((column) => { delete next[column]; });
    return next;
}
